import { User } from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { locationService, notificationService, settingsService, userService } from '@/services';
import { UserSettings } from '@/types';

const ONE_HOUR = 60 * 60 * 1000;

export function useRePromptPermissions(user: User | null): UserSettings | null {
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const promptedFor = useRef<Set<string>>(new Set());
  const lastRefresh = useRef<number>(0);

  useEffect(() => {
    if (!user) return undefined;
    return settingsService.subscribeToSettings(user.uid, setSettings);
  }, [user]);

  useEffect(() => {
    if (!user || !settings || promptedFor.current.has(user.uid)) return;
    promptedFor.current.add(user.uid);

    const uid = user.uid;

    if (settings.notificationsEnabled) {
      void (async () => {
        try {
          const permission = await notificationService.getPermission();
          if (permission === 'granted') {
            await notificationService.registerMessaging(uid);
          } else if (permission === 'default') {
            const newPermission = await notificationService.requestPermission();
            if (newPermission === 'granted') {
              await notificationService.registerMessaging(uid);
            } else {
              await settingsService.updateSettings(uid, { notificationsEnabled: false });
            }
          } else if (permission === 'denied') {
            await settingsService.updateSettings(uid, { notificationsEnabled: false });
          }
        } catch (err) {
          console.error('Failed to re-prompt for notifications:', err);
        }
      })();
    }

    if (settings.locationAllowed) {
      void (async () => {
        try {
          if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
            const result = await navigator.permissions.query({ name: 'geolocation' });
            if (result.state === 'denied') {
              await settingsService.updateSettings(uid, { locationAllowed: false });
              return;
            }
          }

          let permissionDenied = false;
          const coords = await locationService.getCurrentLocation({}, error => {
            permissionDenied = error.code === error.PERMISSION_DENIED;
          });
          if (coords) {
            await userService.updateProfile(uid, { ...coords });
          } else if (permissionDenied) {
            await settingsService.updateSettings(uid, { locationAllowed: false });
          }
        } catch (err) {
          console.error('Failed to re-prompt for location:', err);
        }
      })();
    }
  }, [user, settings]);

  useEffect(() => {
    if (!user || !settings?.notificationsEnabled) return undefined;
    if (typeof document === 'undefined') return undefined;

    const uid = user.uid;
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRefresh.current < ONE_HOUR) return;
      lastRefresh.current = now;

      void notificationService.refreshRegistration(uid).catch(err => {
        console.error('Failed to refresh notification registration:', err);
      });
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user, settings?.notificationsEnabled]);

  return settings;
}
